import { deleteToken, getMessaging, getToken, onMessage, type Messaging } from 'firebase/messaging';
import { firebaseApp } from './firebase';
import { env } from './env';
import { apiFetch } from './api-client';

let _messaging: Messaging | null = null;
function messaging(): Messaging {
  if (_messaging) return _messaging;
  _messaging = getMessaging(firebaseApp());
  return _messaging;
}

// Best-effort client-side token revocation. Sign-out callers should swallow
// errors — push has its own teardown flag and a failure here mustn't strand
// the user mid-sign-out.
export async function revokePushTokenLocally(): Promise<void> {
  if (!isPushSupported()) return;
  try {
    await deleteToken(messaging());
  } catch (e) {
    console.warn('FCM deleteToken failed', (e as any)?.code ?? e);
  }
}

export function isPushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export function getPushPermissionState(): NotificationPermission | 'unsupported' {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

export async function enablePush(): Promise<'granted' | 'denied' | 'default' | 'unsupported'> {
  if (!isPushSupported()) return 'unsupported';

  let permission: NotificationPermission;
  try {
    permission = await Notification.requestPermission();
  } catch (e) {
    console.warn('FCM requestPermission failed', (e as any)?.code ?? e);
    return 'default';
  }
  if (permission !== 'granted') return permission;

  await registerCurrentToken();
  return 'granted';
}

/**
 * Register the current device's FCM token with the API. Used by:
 *  - enablePush() — immediately after the browser permission flips to granted
 *  - refreshPushTokenIfGranted() — on AppShell mount, to catch FCM token
 *    rotation between sessions
 *
 * Idempotent: Redis backing is SADD, so the same token re-registered is a
 * no-op. Errors are swallowed — the worst case is the prior token stays
 * registered until the next session retries.
 */
async function registerCurrentToken(): Promise<void> {
  // Explicit scope avoids collision with the Workbox SW already registered at '/'.
  let swReg: ServiceWorkerRegistration;
  try {
    swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/firebase-cloud-messaging-push-scope',
    });
  } catch (e) {
    console.warn('FCM SW registration failed', (e as any)?.code ?? e);
    return;
  }

  let token: string | null;
  try {
    token = await getToken(messaging(), {
      vapidKey: env().VITE_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
  } catch (e) {
    console.warn('FCM getToken failed', (e as any)?.code ?? e);
    return;
  }
  if (!token) return;

  try {
    await apiFetch('/driver-app/me/push-token', {
      method: 'POST',
      body: JSON.stringify({ token, platform: 'web' }),
    });
  } catch (e) {
    console.warn('push-token register failed', (e as any)?.code ?? e);
  }
}

/**
 * Silently re-register the FCM token if browser permission is already
 * granted. Called from AppShell on mount to catch token rotation between
 * sessions. No UI side effects. Failure is silent.
 */
export async function refreshPushTokenIfGranted(): Promise<void> {
  if (getPushPermissionState() !== 'granted') return;
  await registerCurrentToken();
}

export function onForegroundMessage(
  handler: (msg: { title: string; body: string; data?: Record<string, string> }) => void,
): () => void {
  if (!isPushSupported()) return () => {};
  return onMessage(messaging(), (payload) => {
    handler({
      title: payload.notification?.title ?? '',
      body: payload.notification?.body ?? '',
      data: payload.data,
    });
  });
}
